import { MyAmministrazioneControllo, MyNormeSpaMonistico, MyVerbale } from '../../../../models/interfaces/revisione.interface';

type ArrayWrapper = Record<string, unknown>;

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Array as-is; a single non-empty object (single XML occurrence) is wrapped; anything else (null, '', {}) → []. */
export const ensureArray = <T>(v: unknown): T[] => {
    if (Array.isArray(v)) return v;
    return isPlainObject(v) && Object.keys(v).length > 0 ? [v as T] : [];
};

const ensureObject = <T extends object>(v: unknown, fallback: T): T => (isPlainObject(v) ? (v as T) : fallback);

/** Ensures `parent[wrapperKey][arrayKey]` exists as an array, rebuilding a null/empty/invalid wrapper. */
const ensureWrappedArray = (parent: object, wrapperKey: string, arrayKey: string): void => {
    const holder = parent as Record<string, unknown>;
    const wrapper = ensureObject<ArrayWrapper>(holder[wrapperKey], {});
    wrapper[arrayKey] = ensureArray(wrapper[arrayKey]);
    holder[wrapperKey] = wrapper;
};

/**
 * Normalizes in place every amm-controllo FormArray source (and its parent object) that the API,
 * the XML import or a local backup may deliver as null / {} / missing. The verbale must be mutable.
 */
export const normalizeArraysNorme = (v: MyVerbale): MyVerbale => {
    const sec = v?.my_SezioneI__Rilevazione;
    if (!sec) return v;

    const ac = (sec.my_AmministrazioneControllo = ensureObject(sec.my_AmministrazioneControllo, {} as MyAmministrazioneControllo));
    ac.my_AllAmminUnico = ensureArray(ac.my_AllAmminUnico);
    ensureWrappedArray(ac, 'my_AllConsiglioAmministrazione', 'my_AllElemConsiglioAmministrazione');
    ensureWrappedArray(ac, 'my_AllCollegioSindacale', 'my_AllElemCollegioSindacale');
    ensureWrappedArray(ac, 'my_AllLiquidatori', 'my_AllElemLiquidatori');
    ensureWrappedArray(ac, 'my_AllRevisoriControlloContabile', 'my_AllElemRevisoriControlloContabile');

    ac.my_NormeSpaDualistico = ensureObject(ac.my_NormeSpaDualistico, {} as MyAmministrazioneControllo['my_NormeSpaDualistico']);
    ensureWrappedArray(ac.my_NormeSpaDualistico, 'my_SpdConsiglioSorveglianza', 'my_SpdElemConsiglioSorveglianza');
    ensureWrappedArray(ac.my_NormeSpaDualistico, 'my_SpdConsiglioGestione', 'my_SpdElemConsiglioGestione');

    ac.my_NormeSpaMonistico = ensureObject(ac.my_NormeSpaMonistico, {} as MyNormeSpaMonistico);
    ensureWrappedArray(ac.my_NormeSpaMonistico, 'my_SpmComitatoGestione', 'my_SpmElemComitatoGestione');

    return v;
};
